/**
 * useDemoAccess — is the user currently exploring the isolated Demo Company?
 *
 * The Demo Company is a fully isolated tenant (`company_settings.is_demo`)
 * containing only demonstration data. A prospective customer joins it through
 * `join_demo_company`, which refuses any company that is not flagged as a demo.
 *
 * While the selected company IS that demo tenant, the web/Android subscription
 * redirect is skipped so a new customer can actually look around before
 * choosing a plan. This grants nothing beyond the demo tenant: every query is
 * still constrained by RLS to companies the user is a member of, so their own
 * company remains gated exactly as before, and iOS (App Store paywall rules)
 * is untouched.
 *
 * The demo flag is read from the database — never trusted from local storage
 * alone — so a stored company id cannot be used to bypass the paywall.
 */
import { useEffect, useState } from "react";
import { isDemoCompanyId } from "@/services/demoTenant";

export function useDemoAccess(): { isDemoContext: boolean; loading: boolean } {
  const [isDemoContext, setIsDemoContext] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let selected: string | null = null;
    try {
      selected = window.localStorage.getItem("selected_company_id");
    } catch {
      selected = null;
    }

    if (!selected) {
      setIsDemoContext(false);
      setLoading(false);
      return;
    }

    isDemoCompanyId(selected)
      .then((demo) => { if (!cancelled) setIsDemoContext(demo); })
      .catch(() => { if (!cancelled) setIsDemoContext(false); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { isDemoContext, loading };
}
